import { useState } from 'react';
import { PencilSimple } from '@phosphor-icons/react';
import { gruposVivos, grupoRenombrable, renombrarGrupo } from './muebles';

/**
 * El campo «Grupo» del formulario de una pieza: elegir uno, crear uno nuevo o
 * renombrar el propio.
 *
 * Nace de un reporte de Daniel el 14-oct-2026: «no veo la opción de poder pasar
 * un objeto que importé a otro grupo, o de poder crear o editar otro grupo».
 *
 * Mover y crear YA EXISTÍAN. El campo era un `<input list="…">` con un
 * `<datalist>` de todos los grupos vivos, así que escribir «Comedor» creaba el
 * grupo y elegir de la lista movía la pieza. El problema es que un `datalist`
 * nativo **no pinta ningún indicio** de que hay una lista, y cuando se abre con
 * algo ya escrito **el navegador filtra las opciones por ese texto**: con «Míos»
 * adentro la lista mostraba exactamente una opción, «Míos», que se lee como «no
 * hay más grupos y no lo puedo cambiar». Una función que nadie encuentra no está
 * entregada, así que acá se cambia por un selector de verdad, que muestra
 * SIEMPRE la lista entera.
 *
 * Renombrar sí es nuevo, y va acá porque es donde Daniel lo fue a buscar.
 */
export function SelectorGrupo({ valor, onCambio, onAviso, id }: {
  valor: string;
  onCambio: (g: string) => void;
  /** Para contar al usuario cuántas piezas se movieron al renombrar. */
  onAviso?: (texto: string) => void;
  /** Distingue los dos usos (pantalla general y pestaña del editor) en las pruebas. */
  id: string;
}) {
  // «Grupo nuevo» es un MODO, no un valor: mientras se escribe el nombre, el
  // campo está vacío y no coincide con ninguna opción, así que sin esta bandera
  // el selector se desplomaría a la primera opción de la lista en cada tecla.
  const [nuevo, setNuevo] = useState(false);
  const [renombrando, setRenombrando] = useState(false);

  const grupos = gruposVivos();
  // El grupo de una pieza guardada hace tiempo puede ya no estar en la lista
  // viva (quedó sin piezas y dejó de existir). Se agrega para no moverla de
  // lugar sin que nadie lo pida sólo por abrir el formulario.
  const lista = valor && !grupos.includes(valor) && !nuevo ? [...grupos, valor] : grupos;

  function renombrar(destino: string) {
    const movidas = renombrarGrupo(valor, destino);
    setRenombrando(false);
    if (!movidas) return;
    onCambio(destino.trim());
    onAviso?.(`«${valor}» ahora se llama «${destino.trim()}» — ${movidas} ${movidas === 1 ? 'pieza' : 'piezas'}.`);
  }

  if (nuevo || renombrando) {
    return (
      <label className="flex w-44 flex-col gap-0.5 text-[0.6rem] text-stone-500">
        {renombrando ? `Nuevo nombre de «${valor}»` : 'Grupo nuevo'}
        <input
          autoFocus
          defaultValue={renombrando ? valor : ''}
          data-grupo-nuevo={id}
          placeholder={renombrando ? valor : 'Cómo se llama'}
          onChange={(e) => { if (!renombrando) onCambio(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (renombrando) renombrar((e.target as HTMLInputElement).value);
              else setNuevo(false);
            }
            // Escapar de «grupo nuevo» no puede dejar la pieza sin grupo: se
            // vuelve a la lista y el selector elige el primero que haya.
            if (e.key === 'Escape') {
              e.preventDefault();
              if (renombrando) setRenombrando(false);
              else { setNuevo(false); onCambio(grupos[0] ?? ''); }
            }
          }}
          onBlur={(e) => {
            if (renombrando) renombrar(e.target.value);
            else setNuevo(false);
          }}
          className="rounded border border-stone-300 px-1.5 py-1 text-xs
                     outline-none focus:border-[#5f6f80]"
        />
      </label>
    );
  }

  return (
    <label className="flex w-44 flex-col gap-0.5 text-[0.6rem] text-stone-500">
      Grupo
      <span className="flex items-center gap-1">
        <select
          value={valor}
          data-grupo-objeto={id}
          onChange={(e) => {
            if (e.target.value === '__nuevo__') { setNuevo(true); onCambio(''); return; }
            onCambio(e.target.value);
          }}
          className="min-w-0 flex-1 rounded border border-stone-300 bg-white px-1.5 py-1
                     text-xs outline-none focus:border-[#5f6f80]"
        >
          {lista.map((g) => <option key={g} value={g}>{g}</option>)}
          <option value="__nuevo__">+ Grupo nuevo…</option>
        </select>
        {grupoRenombrable(valor) && (
          <button
            type="button" onClick={() => setRenombrando(true)}
            data-renombrar-grupo={id}
            title={`Renombrar «${valor}» en todas mis piezas`}
            aria-label={`Renombrar el grupo ${valor}`}
            className="shrink-0 rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </span>
    </label>
  );
}
